import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { listingsApi, type ListingDetailDto } from "@/api/buyers";
import { extractErrorMessage } from "@/api/client";
import {
  Button,
  Input,
  Textarea,
  Timestamp,
  useToast,
} from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import { listingKeys } from "@/hooks/buyers/buyerKeys";
import { US_STATES } from "@/lib/usStates";
import {
  buildListingSectionPatch,
  hydrateListingSection,
  listingSectionIsDirty,
  listingSectionNumberErrors,
  sectionHasFigures,
  type ListingFieldConfig,
  type ListingSectionConfig,
  type ListingSectionForm,
} from "./listingSectionConfig";

/**
 * Edit ONE Overview card of a listing.
 *
 * <p>🔴 <b>Per-section, not per-record — and it lives ON the card.</b> Layout
 * Standard §4.1a.1 (v1.5) narrows the Overview rule to workflow actions and says
 * so in terms: "correcting a field on the record you are looking at is data
 * maintenance, not workflow, and belongs where the data is displayed." The
 * licence is ADJACENCY — the value being changed is rendered beside the control
 * that changes it — which is why each modal carries exactly the fields its card
 * shows and no others.</p>
 *
 * <p>The patch is FIND-12, a true partial update, so only this section's keys go
 * on the wire and every field outside it is untouched. A specialist fixing a ZIP
 * code is not handed eighteen fields, seven of which they will read past.</p>
 *
 * <p><b>Built on the shared <c>Modal</c>, which manages its own focus</b> since PR
 * #1043. Two nested traps do not compose, so this passes <c>initialFocusRef</c>
 * and adds nothing of its own — the <c>BuyerSectionEditModal</c> precedent, whose
 * shape this follows throughout.</p>
 *
 * <p><b>The backdrop does not dismiss it.</b> A half-typed form is exactly the
 * state a stray click lands in.</p>
 */
export function ListingSectionEditModal({
  section,
  listing,
  open,
  onClose,
}: {
  section: ListingSectionConfig;
  listing: ListingDetailDto;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const firstFieldRef = useRef<HTMLElement | null>(null);

  const [form, setForm] = useState<ListingSectionForm>(() =>
    hydrateListingSection(section, listing),
  );
  const [error, setError] = useState<string | null>(null);

  // Rehydrate each time it opens, so a cancelled edit is never re-offered as if
  // it were the record's current state.
  useEffect(() => {
    if (!open) return;
    setForm(hydrateListingSection(section, listing));
    setError(null);
  }, [open, section, listing]);

  const numberErrors = listingSectionNumberErrors(section, form);
  const dirty = listingSectionIsDirty(section, listing, form);
  const canSave = dirty && Object.keys(numberErrors).length === 0;

  const save = useMutation({
    mutationFn: () =>
      listingsApi.update(listing.id, buildListingSectionPatch(section, form)),
    onSuccess: () => {
      // The whole listing prefix: this record is read by the detail query, by
      // every list that renders its name, and by the drawer a buyer's shortlist
      // opens it in. The shared QueryClient sits at a five-minute staleTime with
      // no refetch on focus, so a missed invalidation is stale for minutes with
      // no route to recovery.
      void qc.invalidateQueries({ queryKey: listingKeys.all });
      toast.success(`${section.title} updated.`);
      onClose();
    },
    onError: (e: unknown) => setError(extractErrorMessage(e)),
  });

  return (
    <Modal
      open={open}
      onClose={() => (save.isPending ? undefined : onClose())}
      title={`Edit ${section.title.toLowerCase()}`}
      description={section.editDescription}
      size="md"
      dismissOnBackdropClick={false}
      initialFocusRef={firstFieldRef}
      footer={
        <div className="flex justify-end gap-space-2">
          <Button variant="text" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              setError(null);
              save.mutate();
            }}
            loading={save.isPending}
            disabled={save.isPending || !canSave}
            data-testid={`listing-section-save-${section.id}`}
          >
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3">
        {section.fields.map((field, index) => (
          <ListingFieldInput
            key={field.key}
            field={field}
            value={form[field.key] ?? ""}
            error={numberErrors[field.key]}
            onChange={(next) => setForm((f) => ({ ...f, [field.key]: next }))}
            inputRef={index === 0 ? firstFieldRef : undefined}
            disabled={save.isPending}
          />
        ))}

        {sectionHasFigures(section) && (
          /*
            🔴 CARRIED FORWARD VERBATIM from the whole-record form this replaced,
            because the limitation it describes is unchanged: `UpdateListingRequest`
            has exactly one clear flag (`ClearListingAgent`) and writes the seven
            numeric columns `request.X ?? listing.X`, so blanking a figure is not
            expressible over this wire. Strings clear when emptied; numbers do not.
            Without the caption, erasing a wrong figure looks like it saved and did
            not — and the honest thing is to say what the form cannot do rather
            than to let the new modals imply they fixed it.

            Driven off the SECTION, not hard-coded onto Financials: year
            established and employee count are figures on the Business card, so a
            caption on one modal would leave two fields silently dishonest.
          */
          <p className="md:col-span-2 text-caption text-gray-mid m-0">
            Clearing a figure leaves the saved value as it is. Type the correct
            number instead.
          </p>
        )}

        {section.id === "source" && <SourceDerivedFacts listing={listing} />}

        {error && (
          <p className="md:col-span-2 text-small text-status-error m-0" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}

/**
 * The two Source rows that are READ-ONLY for life.
 *
 * <p><b>Dedupe key</b> is derived server-side from the listing URL
 * (<c>ListingService</c> normalises it on write) and is what duplicate detection
 * compares on — a hand-edited one would silently stop matching the listings it is
 * supposed to collide with, and the specialist editing it would have no way to
 * know. <b>Added</b> is write-once.</p>
 *
 * <p>Shown here rather than omitted because the card shows them: a modal that
 * quietly drops two of its card's six rows reads as a bug, and "you cannot change
 * this, here is why" is the thing the reader actually needs.</p>
 */
function SourceDerivedFacts({ listing }: { listing: ListingDetailDto }) {
  return (
    <div className="md:col-span-2 grid gap-space-2 border-t border-border-light pt-space-3">
      <p className="text-caption text-gray-mid m-0">
        Derived from the record and not editable. The dedupe key is recomputed
        from the listing URL when you save it.
      </p>
      <div className="grid grid-cols-[9rem_1fr] gap-space-2">
        <span className="text-caption uppercase tracking-wide text-gray-mid">
          Dedupe key
        </span>
        <span className="text-small text-onyx break-all">
          {listing.normalizedUrl || <span className="text-gray-mid">—</span>}
        </span>
        <span className="text-caption uppercase tracking-wide text-gray-mid">Added</span>
        <span className="text-small text-onyx">
          <Timestamp value={listing.createdAt} />
        </span>
      </div>
    </div>
  );
}

function ListingFieldInput({
  field,
  value,
  error,
  onChange,
  inputRef,
  disabled,
}: {
  field: ListingFieldConfig;
  value: string;
  error?: string;
  onChange: (next: string) => void;
  inputRef?: React.MutableRefObject<HTMLElement | null>;
  disabled: boolean;
}) {
  const id = `listing-field-${field.key}`;
  const wide = field.wide || field.type === "textarea";

  return (
    <label className={wide ? "block md:col-span-2" : "block"} htmlFor={id}>
      <span className="text-caption uppercase tracking-wide text-gray-mid">
        {field.label}
      </span>
      <div className="mt-space-1">
        {field.type === "textarea" ? (
          <Textarea
            id={id}
            rows={5}
            value={value}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            ref={inputRef as React.Ref<HTMLTextAreaElement>}
            data-testid={id}
          />
        ) : field.type === "state" ? (
          <Select
            id={id}
            value={value}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            ref={inputRef as React.Ref<HTMLSelectElement>}
            data-testid={id}
          >
            <option value="">—</option>
            {US_STATES.map((s) => (
              <option key={s.code} value={s.code}>
                {s.name} ({s.code})
              </option>
            ))}
          </Select>
        ) : (
          <Input
            id={id}
            // 🔴 A figure is a TEXT input, never `type="number"`. A number input
            // discards a value the browser considers malformed on blur, which is
            // exactly how a mistyped asking price becomes a silent no-op.
            // `inputMode="decimal"` still raises the numeric keypad on touch.
            inputMode={field.type === "number" ? "decimal" : undefined}
            value={value}
            placeholder={field.hint}
            invalid={Boolean(error)}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            ref={inputRef as React.Ref<HTMLInputElement>}
            data-testid={id}
          />
        )}
      </div>
      {error && (
        <span className="text-caption text-status-error" role="alert">
          {error}
        </span>
      )}
    </label>
  );
}
